import React from 'react';
import {
  ArrowLeft,
  GitBranch,
  Lightbulb,
  Copy,
  Check,
  Landmark,
  Wallet,
  CreditCard,
  Building2,
  Upload,
  X,
  Plus,
  AlertTriangle,
  AlertCircle,
  CircleCheck,
  ScanLine,
  Loader2,
  FileText,
  Ban,
  Info,
} from 'lucide-react';
import {
  auctionPayment,
  initiateAuctionPayment,
  getUserDetails,
} from '../services/mzadcomPaymentService';
import { convertPdfToImage } from '../../../../../utils/pdfToImage';
import { scanReceipt } from '../../../../home/pages/Enroll/services/enrollService';
import '../../../../../styles/components/payment-distribution.scss';
// Shared with the enrollment / wallet-recharge online-payment flows — reused
// here for the fee-summary card, Terms & Conditions checkbox and modal
// (mze-fee-summary / mze-tnc-block / mze-ack-* / mze-modal-* classnames)
// instead of duplicating that CSS under a pd- prefix.
import '../../../../../styles/components/enroll-page.scss';
import { OMR } from '../../../../common/icons';
import { CustomModal } from '../../../../common/CustomModal';
import { toast } from 'react-toastify';
import i18n from '../../../../../i18n';
import { NavLink } from 'react-router-dom';

/* ----------------------------------------------------------------------
 * Same real wiring as before — one instance handles ONE `type`
 * ('client' | 'mzadcom') leg at a time, `auctions` are leg rows whose
 * `balance_amount` / `total_amount` already reflect only this leg's
 * portion (see AuctionPaymentDetail.handleProceed).
 *
 * ALLOCATION MODEL:
 *   The page lists every selected auction with its own pending (due)
 *   amount. Each auction's payment amount is independently editable and
 *   can be any value from 0 up to that auction's own due amount — there
 *   is no fixed pool that has to be fully allocated across the list.
 *   Paying several auctions at once (each for whatever amount the bidder
 *   chooses, including partial amounts) is the normal case, not a special
 *   one. "Reset to Full Amount" re-fills every row back to its full due
 *   amount as a convenience, nothing more.
 *
 * UI otherwise matches the approved reference design:
 *   - "Receives" row showing what this leg is for (Final Bid + VAT /
 *     Service Charge + VAT).
 *   - IBAN / SWIFT added to the bank details block (still placeholder
 *     data from the auction row until a real bank-account source is
 *     wired in — same caveat as bank name / account number already had).
 *   - Wallet method carries a "Recommended" badge + a "Top Up Wallet"
 *     action when the balance is short.
 *
 *   type="client"  -> bank-transfer only. No backend endpoint exists yet
 *                     for this leg — see handleSubmit.
 *   type="mzadcom" -> wallet / online / offline, same real
 *                     auctionPayment() submission as before.
 * -------------------------------------------------------------------- */

function money(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function makeEmptyReceipt() {
  return {
    id: Date.now() + Math.random(),
    amount: '',
    receipt: '',
    transferDate: '',
    receiptImage: null,
    previewUrl: null,
  };
}

const RECEIPT_NUMBER_PATTERN = /^[A-Za-z0-9!@#$%^&*()_\-+=[\]{}:;"'<>?,./]{4,}$/;
const MAX_RECEIPT_ENTRIES = 10;

// Fixed, non-refundable documentation & administration fee charged on every
// ONLINE auction payment, in addition to the allocated payment amount.
// Mirrors ONLINE_PROCESSING_FEE used for wallet recharge / enrollment.
const ONLINE_PROCESSING_FEE = 10;

// Mzadcom has ONE fixed bank account, shared by every mzadcom-leg payment —
// it should eventually come from a real source (settings/config endpoint)
// passed in via the `mzadcomBankDetails` prop. Until that's wired up, this
// fallback is shown instead of an empty/"not configured" state so the
// offline-transfer flow is actually usable today.
const MZADCOM_BANK_DETAILS_FALLBACK = {
  bank_name: 'Bank Muscat',
  account_name: 'Mzadcom Smart Auction Solutions LLC',
  account_number: '0440061839220015',
  iban: 'OM360270440061839220015',
  swift: 'BMUSOMRX',
  currency: 'OMR',
  branch: 'Ghala',
};

class CopyRow extends React.Component {
  state = { copied: false };

  handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(this.props.value);
    } catch (e) {
      // clipboard may be unavailable — fail silently
    }
    this.setState({ copied: true });
    window.clearTimeout(this._t);
    this._t = window.setTimeout(() => this.setState({ copied: false }), 1400);
  };

  componentWillUnmount() {
    window.clearTimeout(this._t);
  }

  render() {
    const { label, value } = this.props;
    if (!value) return null;
    return (
      <div className="pd-bank-row">
        <span className="pd-bank-row__label">{label}</span>
        <span className="pd-bank-row__value">
          {value}
          <button
            type="button"
            className="pd-copy-btn"
            onClick={this.handleCopy}
            aria-label={`Copy ${label}`}
          >
            {this.state.copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </span>
      </div>
    );
  }
}

class PaymentDistribution extends React.Component {
  constructor(props) {
    super(props);

    const auctions = props.auctions || [];
    const editableBalances = {};
    auctions.forEach((a) => {
      editableBalances[a.id] = num(a.balance_amount);
    });
    // Informational only — the sum of what's currently due across the
    // selected auctions. The bidder is NOT required to allocate exactly
    // this much; each auction's payment amount can independently be
    // anywhere from 0 up to its own due amount.
    const totalDue = auctions.reduce((sum, a) => sum + num(a.balance_amount), 0);

    this.state = {
      editableBalances,
      totalDue,

      // mzadcom leg only
      loadingProfile: props.type === 'mzadcom',
      walletBalance: 0,
      method: props.type === 'mzadcom' ? 'offline' : '', // wallet | online | offline

      // shared (bank-transfer form — used by client leg always, and by
      // mzadcom leg when method === 'offline')
      bankName: props.user?.bank || '',
      bankNameError: '',
      entries: [],
      fieldErrors: {},
      entryScanStates: {},
      draggingId: null,

      submitting: false,

      // Online payment — Terms & Conditions / fee disclosure acceptance
      showOnlineTermsModal: false,
      onlineTermsUnlocked: false, // becomes true once the user taps "I Agree" in the modal
      onlineTermsAccepted: false, // reflects the checkbox state
    };
  }

  componentDidMount() {
    if (this.props.type === 'mzadcom' && this.props.open) {
      this.fetchWalletBalance();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      // reset per-open state and reseed from the latest auctions
      const auctions = this.props.auctions || [];
      const editableBalances = {};
      auctions.forEach((a) => {
        editableBalances[a.id] = num(a.balance_amount);
      });
      const totalDue = auctions.reduce((sum, a) => sum + num(a.balance_amount), 0);

      this.setState({
        editableBalances,
        totalDue,
        method: this.props.type === 'mzadcom' ? 'offline' : '',
        entries: [],
        fieldErrors: {},
        entryScanStates: {},
        draggingId: null,
        bankNameError: '',
        showOnlineTermsModal: false,
        onlineTermsUnlocked: false,
        onlineTermsAccepted: false,
      });
      if (this.props.type === 'mzadcom') this.fetchWalletBalance();
    }
  }

  componentWillUnmount() {
    this.state.entries.forEach((e) => {
      if (e.previewUrl) URL.revokeObjectURL(e.previewUrl);
    });
  }

  fetchWalletBalance = async () => {
    this.setState({ loadingProfile: true });
    try {
      const response = await getUserDetails();
      if (response.success) {
        this.setState({ walletBalance: parseFloat(response.data.wallet_amount || 0) });
      }
    } catch (error) {
      console.error('Failed to fetch wallet balance', error);
    } finally {
      this.setState({ loadingProfile: false });
    }
  };

  get auctions() {
    return this.props.auctions || [];
  }

  /** Mzadcom's actual bank account: whatever's passed in via props, falling
   * back to the placeholder above until a real source is wired up. */
  get mzadcomBankDetails() {
    return this.props.mzadcomBankDetails || MZADCOM_BANK_DETAILS_FALLBACK;
  }

  /** Whether this leg represents only a slice of the auction (bid+VAT vs
   * service-charge+VAT) rather than the whole auction amount — determines
   * the wording used in the header/"Receives" row. */
  get isSplitLeg() {
    return this.auctions.some(
      (a) => a.is_client_auction_payment === true || Number(a.is_client_auction_payment) === 1
    );
  }

  get receivesLabel() {
    if (this.props.type === 'client') return 'Final Bid + VAT';
    return this.isSplitLeg ? 'Service Charge + VAT' : 'Full Auction Amount';
  }

  get contextLabel() {
    if (this.props.type === 'client') return 'Paying Bid + VAT to Client';
    return this.isSplitLeg ? 'Paying Service Charge + VAT to Mzadcom' : 'Paying to Mzadcom Account';
  }

  legLabelFor = (auction) => {
    if (this.props.type === 'client') {
      return `Bid ${money(auction.bid_amount)} + VAT ${money(auction.item_amount_vat)}`;
    }
    if (this.isSplitLeg) {
      return `Service ${money(auction.service_charge_amount)} + VAT ${money(auction.vat_service_charge)}`;
    }
    return 'Full auction amount';
  };

  getAllocatedTotal = () => {
    return this.auctions.reduce(
      (sum, a) => sum + (Number(this.state.editableBalances[a.id]) || 0),
      0
    );
  };

  /** Whether at least one auction currently has a payment amount entered.
   * There's no requirement to fill every row or to hit any particular
   * total — this only guards against submitting an all-zero payment. */
  hasAllocation = () => this.getAllocatedTotal() > 0.005;

  handleAllocationChange = (auction, value) => {
    const due = num(auction.balance_amount);

    // Allow empty value while typing
    if (value === '') {
      this.setState((prev) => ({
        editableBalances: {
          ...prev.editableBalances,
          [auction.id]: '',
        },
      }));
      return;
    }

    let next = parseFloat(value);

    if (isNaN(next)) next = 0;
    if (next < 0) next = 0;
    if (next > due) next = due;

    // Maximum 3 decimal places
    next = Math.floor(next * 1000) / 1000;

    this.setState((prev) => ({
      editableBalances: {
        ...prev.editableBalances,
        [auction.id]: next,
      },
    }));
  };

  /** Convenience: re-fills every row back to its own full due amount. */
  handleResetToFullAmount = () => {
    const editableBalances = {};
    this.auctions.forEach((a) => {
      editableBalances[a.id] = num(a.balance_amount);
    });
    this.setState({ editableBalances });
  };

  /* ---------------------------------------------------------------- *
   * Online payment: Terms & Conditions / fee disclosure
   * ---------------------------------------------------------------- */

  openOnlineTermsModal = () => {
    this.setState({ showOnlineTermsModal: true });
  };

  closeOnlineTermsModal = () => {
    this.setState({ showOnlineTermsModal: false });
  };

  // Tapping "I Agree" both unlocks and checks the acceptance checkbox.
  // The user can still uncheck it afterwards to revoke consent, which
  // re-blocks confirmation (see canConfirm / handleSubmit).
  handleAgreeOnlineTerms = () => {
    this.setState({
      showOnlineTermsModal: false,
      onlineTermsUnlocked: true,
      onlineTermsAccepted: true,
    });
  };

  toggleOnlineTermsAccepted = () => {
    if (!this.state.onlineTermsUnlocked) return;
    this.setState((prev) => ({ onlineTermsAccepted: !prev.onlineTermsAccepted }));
  };

  getOnlineFeeBreakdown = () => {
    const amount = this.getAllocatedTotal();
    const fee = ONLINE_PROCESSING_FEE;
    const subtotal = amount + fee;
    return { amount, fee, subtotal };
  };

  /* ---------------------------------------------------------------- *
   * Receipt entries
   * ---------------------------------------------------------------- */

  addEntry = () => {
    const { entries } = this.state;

    if (entries.length >= MAX_RECEIPT_ENTRIES) {
      toast.warning(`Maximum ${MAX_RECEIPT_ENTRIES} receipts allowed.`);
      return;
    }

    const last = entries[entries.length - 1];
    if (last && (!last.amount || !last.receipt || !last.receiptImage)) {
      toast.info('Please complete the current receipt before adding a new one.');
      return;
    }

    this.setState({ entries: [...entries, makeEmptyReceipt()] });
  };

  removeEntry = (id) => {
    const { entries } = this.state;
    const removed = entries.find((e) => e.id === id);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    this.setState({ entries: entries.filter((e) => e.id !== id) });
  };

  updateEntry = (id, field, value) => {
    this.setState((prev) => ({
      entries: prev.entries.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
      fieldErrors: {
        ...prev.fieldErrors,
        [id]: { ...(prev.fieldErrors[id] || {}), [field]: undefined },
      },
    }));
  };

  handleFileUpload = (id, file) => {
    if (!file) return;

    const maxFileSize = 10 * 1024 * 1024;
    if (file.size > maxFileSize) {
      toast.error('File size must be less than 10MB.');
      return;
    }

    const isDuplicate = this.state.entries.some(
      (e) =>
        e.id !== id &&
        e.receiptImage &&
        e.receiptImage.name === file.name &&
        e.receiptImage.size === file.size &&
        e.receiptImage.lastModified === file.lastModified
    );
    if (isDuplicate) {
      toast.error('This file is already uploaded in another receipt.');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    this.setState((prev) => ({
      entries: prev.entries.map((e) =>
        e.id === id ? { ...e, receiptImage: file, previewUrl } : e
      ),
      fieldErrors: {
        ...prev.fieldErrors,
        [id]: { ...(prev.fieldErrors[id] || {}), receiptImage: undefined },
      },
    }));
  };

  removeFile = (id) => {
    this.setState((prev) => ({
      entries: prev.entries.map((e) => {
        if (e.id === id && e.previewUrl) {
          URL.revokeObjectURL(e.previewUrl);
          return { ...e, receiptImage: null, previewUrl: null };
        }
        return e;
      }),
    }));
  };

  handleReceiptScan = async (id) => {
    const entry = this.state.entries.find((e) => e.id === id);
    if (!entry?.receiptImage) return;

    this.setState((prev) => ({
      entryScanStates: {
        ...prev.entryScanStates,
        [id]: { isScanning: true, scanSuccess: false, scanError: null },
      },
    }));

    try {
      let uploadFile = entry.receiptImage;
      if (entry.receiptImage.type === 'application/pdf') {
        uploadFile = await convertPdfToImage(entry.receiptImage);
      }

      const formData = new FormData();
      formData.append('receipt', uploadFile);
      const result = await scanReceipt(formData);

      if (result.success && result.data) {
        const { bank_name, receipt_number, amount, transfer_date } = result.data.form;

        this.setState((prev) => ({
          entries: prev.entries.map((e) =>
            e.id === id
              ? {
                  ...e,
                  receipt: receipt_number || e.receipt,
                  amount: amount || e.amount,
                  transferDate: transfer_date || e.transferDate,
                }
              : e
          ),
          bankName: bank_name || prev.bankName,
          bankNameError: bank_name ? '' : prev.bankNameError,
          entryScanStates: {
            ...prev.entryScanStates,
            [id]: { isScanning: false, scanSuccess: true, scanError: null },
          },
          fieldErrors: {
            ...prev.fieldErrors,
            [id]: {
              ...(prev.fieldErrors[id] || {}),
              receipt: undefined,
              amount: undefined,
              transferDate: undefined,
            },
          },
        }));

        toast.success('Receipt scanned successfully! Please verify the fields.');
      } else {
        this.setState((prev) => ({
          entryScanStates: {
            ...prev.entryScanStates,
            [id]: {
              isScanning: false,
              scanSuccess: false,
              scanError: 'Could not extract data. Please fill manually.',
            },
          },
        }));
      }
    } catch (err) {
      console.error('[ReceiptScan]', err);
      this.setState((prev) => ({
        entryScanStates: {
          ...prev.entryScanStates,
          [id]: {
            isScanning: false,
            scanSuccess: false,
            scanError: err.message || 'Scan failed. Please fill the form manually.',
          },
        },
      }));
    }
  };

  /** True once nothing is left half-filled — used to gate "Add Receipt"
   * when the list is empty (nothing pending, always allowed) or the last
   * entry is complete. */
  lastReceiptComplete = () => {
    const { entries } = this.state;
    if (entries.length === 0) return true;
    const last = entries[entries.length - 1];
    return Boolean(last.receiptImage && last.transferDate && last.amount);
  };

  validateEntries = (totalAmount) => {
    const { entries, bankName } = this.state;
    let valid = true;

    if (!bankName || bankName.trim() === '') {
      this.setState({ bankNameError: 'Bank name is required.' });
      valid = false;
    } else {
      this.setState({ bankNameError: '' });
    }

    if (entries.length === 0) {
      toast.error('Add at least one receipt.');
      valid = false;
    }

    const receiptNumbers = entries.map((e) => e.receipt.trim().toUpperCase()).filter(Boolean);
    const duplicates = receiptNumbers.filter((r, i) => receiptNumbers.indexOf(r) !== i);
    if (duplicates.length > 0) {
      toast.error('Receipt numbers must be unique.');
      valid = false;
    }

    const newFieldErrors = {};
    entries.forEach((entry) => {
      const errors = {};
      if (!entry.transferDate) errors.transferDate = 'Required';
      if (!entry.amount || parseFloat(entry.amount) <= 0) errors.amount = 'Required';
      if (!entry.receipt || entry.receipt.trim() === '') {
        errors.receipt = 'Required';
      } else if (!RECEIPT_NUMBER_PATTERN.test(entry.receipt)) {
        errors.receipt = 'Must be at least 4 characters';
      }
      if (!entry.receiptImage) errors.receiptImage = 'Required';

      if (Object.keys(errors).length) {
        newFieldErrors[entry.id] = errors;
        valid = false;
      }
    });
    this.setState({ fieldErrors: newFieldErrors });

    if (valid) {
      const totalEntries = entries.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      if (Math.abs(totalEntries - totalAmount) > 0.01) {
        toast.warning(
          `Total receipts amount (${totalEntries.toFixed(2)}) should match the payment amount (${totalAmount.toFixed(2)}).`
        );
        valid = false;
      }
    }

    return valid;
  };

  buildEntriesFormData = (formData) => {
    formData.append('bankName', this.state.bankName);
    this.state.entries.forEach((entry, index) => {
      formData.append(`payments[${index}][amount]`, parseFloat(entry.amount).toFixed(2));
      formData.append(`payments[${index}][receiptNumber]`, entry.receipt.trim());
      formData.append(`payments[${index}][transferDate]`, entry.transferDate || '');
      formData.append(`payments[${index}][receiptImage]`, entry.receiptImage);
    });
    formData.append('paymentsCount', this.state.entries.length);
  };

  /* ---------------------------------------------------------------- *
   * Submission
   * ---------------------------------------------------------------- */

  canPayWithWallet = () => {
    const total = this.getAllocatedTotal();
    return total > 0 && total <= this.state.walletBalance;
  };

  canPayOnline = () => {
    return this.getAllocatedTotal() < 5000;
  };

  /** Drives whether the Confirm button is enabled — at least one auction
   * needs a payment amount entered, plus whatever the chosen method (or
   * the client leg's bank-transfer form) additionally needs. */
  canConfirm = () => {
    const { type } = this.props;
    const { method, entries, onlineTermsAccepted } = this.state;

    if (!this.hasAllocation()) return false;

    const entriesComplete = () =>
      entries.length > 0 &&
      entries.every((e) => e.receiptImage && e.transferDate && e.amount && e.receipt);

    if (type === 'client') return entriesComplete();

    if (method === 'wallet') return this.canPayWithWallet();
    // Online additionally requires the fee-disclosure Terms & Conditions
    // to have been accepted before it can be confirmed.
    if (method === 'online') return this.canPayOnline() && onlineTermsAccepted;
    if (method === 'offline') return entriesComplete();
    return false;
  };

  handleSubmit = async () => {
    const { type } = this.props;
    const { method, editableBalances, onlineTermsAccepted } = this.state;
    const totalAmount = this.getAllocatedTotal();

    if (totalAmount <= 0) {
      toast.error('Enter a payment amount for at least one auction.');
      return;
    }

    const paymentMethod = type === 'client' ? 'offline' : method;

    if (type === 'mzadcom' && !method) {
      toast.error('Please select a payment method.');
      return;
    }
    if (paymentMethod === 'offline' && !this.validateEntries(totalAmount)) return;
    if (paymentMethod === 'online' && !this.canPayOnline()) {
      toast.error('Online payment is only available for amounts less than 5000.');
      return;
    }
    if (paymentMethod === 'online' && !onlineTermsAccepted) {
      toast.error(
        i18n.t('please_accept_terms_and_conditions') ||
          'Please accept the Terms & Conditions to continue.'
      );
      return;
    }
    if (paymentMethod === 'wallet' && !this.canPayWithWallet()) {
      toast.error('Insufficient wallet balance.');
      return;
    }

    // Online additionally charges a flat, non-refundable documentation/admin
    // fee on top of the allocated total (see getOnlineFeeBreakdown /
    // ONLINE_PROCESSING_FEE). `subtotal` is the FINAL amount actually paid —
    // for online that's the fee-inclusive total; for every other method it's
    // just the allocated total, unchanged.
    const { subtotal: onlinePayable } = this.getOnlineFeeBreakdown();
    const finalPayableAmount = paymentMethod === 'online' ? onlinePayable : totalAmount;

    const formData = new FormData();
    this.auctions.forEach((auction, index) => {
      formData.append(`auctions[${index}][id]`, auction.id);
      formData.append(`auctions[${index}][amount]`, editableBalances[auction.id] || 0);
    });
    formData.append('subtotal', finalPayableAmount);
    formData.append('paymentMethod', paymentMethod);
    formData.append('destination', type); // NOTE: for type="client", backend needs to branch on this — no endpoint exists yet, see note below.

    if (paymentMethod === 'offline') {
      this.buildEntriesFormData(formData);
    }

    this.setState({ submitting: true });
    try {
      // Mzadcom online payments go through their own initiation endpoint
      // (initiateAuctionPayment) — same pattern as wallet recharge / auction
      // enrollment online payments — since it's the one that has to apply
      // the documentation/admin fee and kick off the payment-gateway
      // redirect. Every other method (wallet / offline, plus the client leg)
      // keeps using the existing auctionPayment() endpoint.
      //
      // NOTE: auctionPayment() has only ever settled funds to Mzadcom's own
      // account. For type="client" this needs either a dedicated endpoint
      // or an updated auctionPayment() that branches on `destination` —
      // wire that up before this goes live for the client leg.
      const response =
        paymentMethod === 'online'
          ? await initiateAuctionPayment(formData)
          : await auctionPayment(formData);
      if (response.success) {
        toast.success(response.message || 'Payment successful');
        this.props.onSubmit?.(response);
      } else {
        toast.error(response.message || 'Payment failed');
      }
    } catch (error) {
      this.handleSubmitError(error);
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleSubmitError = (error) => {
    console.error(error);
    const res = error?.response?.data || error;
    const backendErrors = res?.data || res?.errors || {};
    const { entries } = this.state;

    const newFieldErrors = {};
    let globalMessage = res?.message || 'Something went wrong';

    Object.keys(backendErrors).forEach((key) => {
      const messages = backendErrors[key];
      const message = Array.isArray(messages) ? messages[0] : messages;
      const match = key.match(/^payments\.(\d+)\.(.+)$/);

      if (match) {
        const index = parseInt(match[1], 10);
        const field = match[2];
        const entryId = entries?.[index]?.id;
        if (entryId) {
          if (!newFieldErrors[entryId]) newFieldErrors[entryId] = {};
          const fieldMap = {
            receiptNumber: 'receipt',
            receiptImage: 'receiptImage',
            amount: 'amount',
            transferDate: 'transferDate',
          };
          if (fieldMap[field]) newFieldErrors[entryId][fieldMap[field]] = message;
        }
      } else {
        globalMessage = message;
      }
    });

    if (Object.keys(newFieldErrors).length > 0) {
      this.setState({ fieldErrors: newFieldErrors });
    }
    toast.error(globalMessage);
  };

  handleTopUpWallet = () => {
    // TODO: wire this up to the real wallet top-up flow once it exists.
    this.props.onTopUpWallet?.();
  };

  /* ---------------------------------------------------------------- *
   * Rendering
   * ---------------------------------------------------------------- */

  renderEntryError = (entryId, field) => {
    const msg = this.state.fieldErrors?.[entryId]?.[field];
    if (!msg) return null;
    return (
      <span className="pd-field-error">
        <AlertCircle size={12} /> {msg}
      </span>
    );
  };

  // ── Online payment: payment amount / fee / subtotal breakdown ──
  renderFeeSummary = () => {
    const { amount, fee, subtotal } = this.getOnlineFeeBreakdown();
    const hasAmount = amount > 0;

    return (
      <div className="mze-fee-summary">
        <div className="mze-fee-summary__header">
          <Wallet size={14} />
          <span>{i18n.t('payment_summary') || 'Payment Summary'}</span>
        </div>

        <div className="mze-fee-row">
          <span className="mze-fee-row__label">{i18n.t('payment_amount') || 'Payment Amount'}</span>
          <span className="mze-fee-row__value">
            {hasAmount ? amount.toFixed(3) : '0.000'} <OMR size={9} strokeWidth={2} />
          </span>
        </div>

        <div className="mze-fee-row mze-fee-row--fee">
          <span className="mze-fee-row__label">
            {i18n.t('documentation_admin_fee') || 'Documentation & Admin Fee'}
            <span className="mze-nonrefundable-badge">
              <Ban size={10} />
              {i18n.t('non_refundable') || 'Non-refundable'}
            </span>
          </span>
          <span className="mze-fee-row__value mze-fee-row__value--fee">
            {fee.toFixed(3)} <OMR size={9} strokeWidth={2} color={'#d97706'} />
          </span>
        </div>

        <div className="mze-fee-row mze-fee-row--total">
          <span className="mze-fee-row__label">
            {i18n.t('subtotal') || 'Subtotal (Payable Now)'}
          </span>
          <span className="mze-fee-row__value">
            {subtotal.toFixed(3)} <OMR size={9} strokeWidth={2} color={'#0c8da2'} />
          </span>
        </div>
      </div>
    );
  };

  // ── Online payment: Terms & Conditions link + checkbox ──
  renderOnlineTermsAcceptance = () => {
    const { onlineTermsUnlocked, onlineTermsAccepted } = this.state;

    return (
      <div className="mze-tnc-block">
        <div className="mze-tnc-link-row">
          <button type="button" className="mze-tnc-link" onClick={this.openOnlineTermsModal}>
            <FileText size={14} />
            {i18n.t('terms_and_conditions') || 'Terms & Conditions'}
          </button>
          <span className="mze-tnc-link-hint">
            {i18n.t('open_to_read_and_agree') ||
              'Open to read the full text, then tap "I Agree" at the bottom.'}
          </span>
        </div>

        <label className={`mze-check ${!onlineTermsUnlocked ? 'mze-check--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={onlineTermsAccepted}
            disabled={!onlineTermsUnlocked}
            onChange={this.toggleOnlineTermsAccepted}
          />
          <span className="mze-check__box">
            <Check size={12} strokeWidth={3} />
          </span>
          <span className="mze-check__text">
            {i18n.t('i_have_read_and_accept_terms') ||
              'I have read and accept the Terms & Conditions.'}
          </span>
        </label>

        {!onlineTermsUnlocked && (
          <p className="mze-tnc-hint">
            <Info size={12} />
            {i18n.t('checkbox_disabled_hint') ||
              'The checkbox stays disabled until you complete the Terms & Conditions step (I Agree).'}
          </p>
        )}

        {onlineTermsUnlocked && !onlineTermsAccepted && (
          <span className="pd-field-error">
            <AlertCircle size={12} />
            {i18n.t('please_accept_terms_and_conditions') ||
              'Please accept the Terms & Conditions to continue.'}
          </span>
        )}
      </div>
    );
  };

  // ── Online payment: Terms & Conditions modal ──
  renderOnlineTermsModal = () => {
    if (!this.state.showOnlineTermsModal) return null;
    const terms = [i18n.t('ack_term_1'), i18n.t('ack_term_2'), i18n.t('ack_term_3')];

    const footer = (
      <div className="mze-ack-footer">
        <button type="button" className="modal-btn btn-cancel" onClick={this.closeOnlineTermsModal}>
          <X size={14} />
          <span>{i18n.t('close')}</span>
        </button>
        <button
          type="button"
          className="modal-btn btn-confirm"
          onClick={this.handleAgreeOnlineTerms}
        >
          <Check size={14} />
          <span>{i18n.t('i_agree')}</span>
        </button>
      </div>
    );

    return (
      <CustomModal
        isOpen={this.state.showOnlineTermsModal}
        header={i18n.t('auction_agreement_acknowledgement')}
        headerIcon={<FileText size={18} />}
        footer={footer}
        modalClassName="mze-ack-modal"
        closeBtn={false}
      >
        {/* ── Terms ── */}
        <div className="mze-ack-block">
          <div className="mze-ack-block__head">
            <FileText size={13} />
            <span>{i18n.t('acknowledgement_of_terms')}</span>
          </div>
          <div className="mze-ack-block__body mze-ack-block__body--terms">
            {terms.map((term, idx) => (
              <div key={idx} className="mze-ack-term">
                <div className="mze-ack-term__num">{idx + 1}</div>
                <p className="mze-ack-term__text">{term}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Legal notice ── */}
        <div className="mze-ack-notice">
          <AlertCircle size={14} />
          <p>{i18n.t('ack_legal_notice')}</p>
        </div>
      </CustomModal>
    );
  };

  renderAllocationRow = (auction) => {
    const due = num(auction.balance_amount);
    const allocated = Number(this.state.editableBalances[auction.id]) || 0;
    const after = due - allocated;
    const afterTone = Math.abs(after) < 0.005 ? 'zero' : after > 0 ? 'owed' : 'over';

    return (
      <div className="pd-alloc-row" key={auction.id}>
        <div className="pd-alloc-row__identity">
          <div className="auction-id">MZAD {auction.id}</div>
          <span></span>
          <strong title={auction.title}>{auction.title}</strong>
          <span>{this.legLabelFor(auction)}</span>
        </div>

        <div className="pd-alloc-row__due">
          <span className="pd-alloc-row__label">PENDING (DUE)</span>
          <strong>
            <OMR size={12} />
            {money(due)}
          </strong>
        </div>

        <div className="pd-alloc-row__input">
          <span className="pd-alloc-row__label">PAYMENT AMOUNT</span>
          <div className="pd-amount-input">
            <span>
              <OMR size={12} />
            </span>
            <input
              type="number"
              value={this.state.editableBalances[auction.id]}
              onChange={(e) => this.handleAllocationChange(auction, e.target.value)}
              onWheel={(e) => e.target.blur()}
              step="0.01"
              min="0"
              max={due}
            />
          </div>
        </div>

        <div className="pd-alloc-row__after">
          <span className="pd-alloc-row__label">REMAINING AFTER</span>
          <strong className={`pd-alloc-row__after-value pd-alloc-row__after-value--${afterTone}`}>
            <OMR size={12} />
            {money(Math.abs(after))}
          </strong>
        </div>
      </div>
    );
  };

  renderReceiptBlock = (entry, index) => {
    const scanState = this.state.entryScanStates[entry.id] || {};
    const today = new Date().toISOString().split('T')[0];
    const isDragging = this.state.draggingId === entry.id;
    const uploadInputId = `pd-receipt-upload-${entry.id}`;

    return (
      <div className="pd-receipt" key={entry.id}>
        <div className="pd-receipt__head">
          <strong>Receipt {index + 1}</strong>
          <button
            type="button"
            className="pd-receipt__remove"
            onClick={() => this.removeEntry(entry.id)}
            aria-label="Remove receipt"
          >
            <X size={16} />
          </button>
        </div>

        <label className="pd-field-label">
          RECEIPT DOCUMENT <span className="pd-field-required">*</span>
        </label>

        <input
          type="file"
          id={uploadInputId}
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => this.handleFileUpload(entry.id, e.target.files?.[0])}
        />

        {!entry.receiptImage ? (
          <div
            className={`pd-upload-zone${isDragging ? ' pd-upload-zone--dragover' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              this.setState({ draggingId: entry.id });
            }}
            onDragLeave={() => this.setState({ draggingId: null })}
            onDrop={(e) => {
              e.preventDefault();
              this.setState({ draggingId: null });
              this.handleFileUpload(entry.id, e.dataTransfer.files?.[0]);
            }}
          >
            <Upload size={24} className="pd-upload-zone__icon" />
            <p className="pd-upload-zone__title">Drop your receipt here</p>
            <p className="pd-upload-zone__sub">JPG, PNG or PDF — max 10 MB</p>
            <button
              type="button"
              className="pd-upload-zone__btn"
              onClick={() => document.getElementById(uploadInputId).click()}
            >
              Browse File
            </button>
          </div>
        ) : (
          <div className="pd-receipt-preview">
            <div className="pd-receipt-preview__header">
              <FileText size={15} className="pd-receipt-preview__file-icon" />
              <span className="pd-receipt-preview__name">{entry.receiptImage.name}</span>
              <span className="pd-receipt-preview__size">
                {entry.receiptImage.size < 1048576
                  ? Math.round(entry.receiptImage.size / 1024) + ' KB'
                  : (entry.receiptImage.size / 1048576).toFixed(1) + ' MB'}
              </span>
              <button
                type="button"
                className="pd-receipt-preview__remove"
                onClick={() => this.removeFile(entry.id)}
              >
                <X size={12} /> Remove
              </button>
            </div>

            <div
              className={`pd-receipt-preview__body${
                entry.receiptImage.type.startsWith('image/')
                  ? ' pd-receipt-preview__body--image'
                  : ''
              }`}
            >
              {entry.receiptImage.type.startsWith('image/') && (
                <img
                  src={entry.previewUrl}
                  alt="Receipt preview"
                  className="pd-receipt-preview__img"
                />
              )}
              {entry.receiptImage.type === 'application/pdf' && (
                <div className="pd-receipt-preview__pdf">
                  <FileText size={32} className="pd-receipt-preview__pdf-icon" />
                  <div>
                    <p className="pd-receipt-preview__pdf-name">{entry.receiptImage.name}</p>
                    <span className="pd-receipt-preview__pdf-meta">
                      PDF document · {(entry.receiptImage.size / 1048576).toFixed(1)} MB
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="pd-receipt-preview__actions">
              <button
                type="button"
                className="pd-receipt-preview__view-btn"
                onClick={() => window.open(entry.previewUrl, '_blank')}
              >
                <FileText size={13} /> View Full Size
              </button>
              <button
                type="button"
                className="pd-receipt-preview__change-btn"
                onClick={() => document.getElementById(uploadInputId).click()}
              >
                <Upload size={13} /> Change File
              </button>
            </div>
          </div>
        )}

        {this.renderEntryError(entry.id, 'receiptImage')}

        {entry.receiptImage && (
          <button
            type="button"
            className={`pd-ai-scan-btn${scanState.isScanning ? ' pd-ai-scan-btn--scanning' : ''}${
              scanState.scanSuccess ? ' pd-ai-scan-btn--success' : ''
            }`}
            onClick={() => this.handleReceiptScan(entry.id)}
            disabled={scanState.isScanning || scanState.scanSuccess}
          >
            {scanState.isScanning ? (
              <>
                <Loader2 size={16} className="pd-spin" />
                <span>Scanning receipt…</span>
                <span className="pd-ai-scan-btn__dots">
                  <span />
                  <span />
                  <span />
                </span>
              </>
            ) : scanState.scanSuccess ? (
              <>
                <Check size={16} />
                <span>Fields filled</span>
              </>
            ) : (
              <>
                <span className="pd-ai-scan-btn__glow" />
                <ScanLine size={16} />
                <span>Auto-fill from receipt</span>
                <span className="pd-ai-scan-btn__badge">AI</span>
              </>
            )}
          </button>
        )}
        {scanState.scanError && (
          <div className="pd-upload-error">
            <AlertCircle size={13} />
            {scanState.scanError}
          </div>
        )}

        <div className="pd-receipt__grid">
          <div>
            <label className="pd-field-label">TRANSFER DATE</label>
            <input
              type="date"
              max={today}
              className="pd-text-input"
              value={entry.transferDate}
              onChange={(e) => this.updateEntry(entry.id, 'transferDate', e.target.value)}
            />
            {this.renderEntryError(entry.id, 'transferDate')}
          </div>
          <div>
            <label className="pd-field-label">RECEIPT AMOUNT (OMR)</label>
            <input
              type="number"
              className="pd-text-input"
              value={entry.amount}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => this.updateEntry(entry.id, 'amount', e.target.value)}
              step="0.01"
              min="0"
            />
            {this.renderEntryError(entry.id, 'amount')}
          </div>
        </div>

        <label className="pd-field-label">RECEIPT NUMBER</label>
        <input
          type="text"
          className="pd-text-input"
          placeholder="e.g. TRF-88214"
          value={entry.receipt}
          onChange={(e) => this.updateEntry(entry.id, 'receipt', e.target.value)}
        />
        {this.renderEntryError(entry.id, 'receipt')}
      </div>
    );
  };

  renderClientBankDetails = () => {
    const firstAuction = this.auctions?.[0] || {};
    let bank = firstAuction.auction_payment_bank_account || {};

    return (
      <div className="pd-bank-details">
        <CopyRow label="BANK NAME" value={bank.bank_name} />
        <CopyRow label="ACCOUNT NAME" value={bank.account_name} />
        <CopyRow label="ACCOUNT NUMBER" value={bank.account_number} />
        <CopyRow label="IBAN" value={bank.iban} />
        <CopyRow label="SWIFT CODE" value={bank.swift_code} />
        <CopyRow label="CURRENCY" value={bank.currency} />
        <CopyRow label="BRANCH" value={bank.branch_name} />
      </div>
    );
  };

  /** Mzadcom's own fixed bank account — never sourced from the auction row
   * (those fields are the CLIENT's own resolved account, see
   * auction_payment_bank_account_id). Shows real data once passed in via
   * the `mzadcomBankDetails` prop; falls back to a placeholder account
   * until then so the offline-transfer flow works today. */
  renderMzadcomBankDetails = () => {
    const details = this.mzadcomBankDetails;

    return (
      <div className="pd-bank-details">
        <CopyRow label="BANK NAME" value={details.bank_name} />
        <CopyRow label="ACCOUNT NAME" value={details.account_name} />
        <CopyRow label="ACCOUNT NUMBER" value={details.account_number} />
        <CopyRow label="IBAN" value={details.iban} />
        <CopyRow label="SWIFT CODE" value={details.swift} />
        <CopyRow label="CURRENCY" value={details.currency} />
        <CopyRow label="BRANCH" value={details.branch} />
      </div>
    );
  };

  renderClientPanel() {
    const allocatedTotal = this.getAllocatedTotal();
    const firstAuction = this.auctions[0];
    const bankName = firstAuction?.auction_payment_bank_account?.bank_name || 'your bank';

    return (
      <section className="pd-panel pd-destination-panel">
        <div className="pd-destination-head">
          <span className="pd-destination-head__icon pd-destination-head__icon--olive">
            <Landmark size={20} />
          </span>
          <div>
            <p className="pd-destination-head__title">Client Payment</p>
            <p className="pd-destination-head__sub">
              Destination: {firstAuction?.organization_name || firstAuction?.client_name}
            </p>
          </div>
        </div>

        <div className="pd-receive-row">
          <span>Receives</span>
          <strong>{this.receivesLabel}</strong>
        </div>
        <div className="pd-receive-row">
          <span>Amount</span>
          <strong className="pd-receive-row__amount">{money(allocatedTotal)} OMR</strong>
        </div>

        {/* Bank details below are placeholders wired to the auction's own
            bank fields per the current API response. Flagged by product as
            "dummy for now, will be made dynamic later" — no change needed
            here until that data source is finalized. */}
        {this.renderClientBankDetails()}

        <span className="pd-only-pill">
          <Building2 size={14} /> Offline Bank Transfer Only
        </span>

        <p className="pd-receipts-title">
          Receipts for transfer to <strong>{bankName}</strong>
        </p>
        <div className="form-group">
          <label>{i18n.t('bank_name')} *</label>
          <input
            type="text"
            value={this.state.bankName}
            onChange={(e) => this.setState({ bankName: e.target.value })}
            className={`form-input${this.state.bankNameError ? ' input-error' : ''}`}
            placeholder="Enter the bank you're transferring from"
            required
          />
          {this.state.bankNameError && (
            <span className="mze-field__error">
              <AlertCircle size={12} /> {this.state.bankNameError}
            </span>
          )}
        </div>
        {this.state.entries.map((e, idx) => this.renderReceiptBlock(e, idx))}

        <button
          type="button"
          className="pd-add-receipt"
          onClick={this.addEntry}
          disabled={!this.lastReceiptComplete()}
        >
          <Plus size={16} /> Add Receipt
        </button>
      </section>
    );
  }

  renderMzadcomPanel() {
    const { method, walletBalance, loadingProfile } = this.state;
    const allocatedTotal = this.getAllocatedTotal();
    const bankName = this.mzadcomBankDetails.bank_name;
    const walletShort = walletBalance < allocatedTotal;

    return (
      <section className="pd-panel pd-destination-panel">
        <div className="pd-destination-head">
          <span className="pd-destination-head__icon pd-destination-head__icon--teal">
            <Landmark size={20} />
          </span>
          <div>
            <p className="pd-destination-head__title">Mzadcom Payment</p>
            <p className="pd-destination-head__sub">Destination: Mzadcom Account</p>
          </div>
        </div>

        <div className="pd-receive-row">
          <span>Receives</span>
          <strong>{this.receivesLabel}</strong>
        </div>
        <div className="pd-receive-row">
          <span>Amount</span>
          <strong className="pd-receive-row__amount">
            {money(allocatedTotal)} <OMR size={12} />
          </strong>
        </div>

        {loadingProfile ? (
          <p className="pd-receipts-title">Loading wallet balance…</p>
        ) : (
          <div className="pd-method-group">
            <button
              type="button"
              className={`pd-method${method === 'offline' ? ' pd-method--active' : ''}`}
              onClick={() => this.setState({ method: 'offline' })}
            >
              <span className="pd-method__badge">Recommended</span>
              <span className="pd-method__icon">
                <Building2 size={18} />
              </span>
              <strong>Offline Bank Transfer</strong>
              <span>Transfer manually and upload your receipt for verification.</span>
            </button>

            <button
              type="button"
              className={`pd-method${method === 'wallet' ? ' pd-method--active' : ''}`}
              onClick={() => this.setState({ method: 'wallet' })}
            >
              <span className="pd-method__icon">
                <Wallet size={18} />
              </span>
              <strong>Wallet</strong>
              <span>Instant payment from your Mzadcom wallet balance.</span>
            </button>

            <button
              type="button"
              // disabled
              className={`pd-method${method === 'online' ? ' pd-method--active' : ''}`}
              onClick={() => this.setState({ method: 'online' })}
            >
              <span className="pd-method__icon">
                <CreditCard size={18} />
              </span>
              <strong>Online Payment</strong>
              <span>Visa, Mastercard, Apple Pay &amp; Google Pay — secure checkout.</span>
            </button>
          </div>
        )}

        {method === 'online' && !this.canPayOnline() && (
          <span className="pd-field-error">
            <AlertCircle size={12} /> Online payment is only available for amounts under 5,000 OMR.
          </span>
        )}

        {method === 'wallet' && (
          <div className="pd-wallet-box">
            <div className="pd-receive-row">
              <span>Current Wallet Balance</span>
              <strong>
                {money(walletBalance)} <OMR size={12} />
              </strong>
            </div>
            <div className="pd-receive-row">
              <span>Payment Amount</span>
              <strong>
                {money(allocatedTotal)} <OMR size={12} />
              </strong>
            </div>
            <div className="pd-receive-row">
              <span>Remaining Wallet After</span>
              <strong className={walletShort ? 'pd-danger-text' : ''}>
                {money(Math.max(walletBalance - allocatedTotal, 0))} <OMR size={12} />
              </strong>
            </div>

            {walletShort && (
              <React.Fragment>
                <div className="pd-warning-banner">
                  <AlertTriangle size={16} />
                  Insufficient wallet balance. Top up your wallet or choose another method.
                </div>
                <NavLink to="/wallet-manage" className="pd-topup-btn">
                  <Plus size={16} /> Top Up Wallet
                </NavLink>
              </React.Fragment>
            )}
          </div>
        )}

        {method === 'online' && (
          <div className="pd-online-box">
            {this.renderFeeSummary()}
            {this.renderOnlineTermsAcceptance()}
            {this.renderOnlineTermsModal()}
          </div>
        )}

        {method === 'offline' && (
          <React.Fragment>
            <div className="pd-receive-row">
              <span>Amount to Transfer</span>
              <strong>
                {money(allocatedTotal)} <OMR size={12} />
              </strong>
            </div>

            {this.renderMzadcomBankDetails()}

            <p className="pd-receipts-title">
              Receipts for transfer to <strong>{bankName}</strong>
            </p>
            <div className="form-group">
              <label>{i18n.t('bank_name')} *</label>
              <input
                type="text"
                value={this.state.bankName}
                onChange={(e) => this.setState({ bankName: e.target.value })}
                className={`form-input${this.state.bankNameError ? ' input-error' : ''}`}
                placeholder="Enter the bank you're transferring from"
                required
              />
              {this.state.bankNameError && (
                <span className="mze-field__error">
                  <AlertCircle size={12} /> {this.state.bankNameError}
                </span>
              )}
            </div>
            {this.state.entries.map((e, idx) => this.renderReceiptBlock(e, idx))}

            <button
              type="button"
              className="pd-add-receipt pd-add-receipt--empty"
              onClick={this.addEntry}
              disabled={!this.lastReceiptComplete()}
            >
              <Plus size={16} /> Add Receipt
            </button>
          </React.Fragment>
        )}
      </section>
    );
  }

  render() {
    const { open, type } = this.props;
    if (!open || this.auctions.length === 0) return null;

    const { method, walletBalance, submitting, totalDue } = this.state;
    const allocatedTotal = this.getAllocatedTotal();
    const hasAllocation = this.hasAllocation();
    const canConfirm = this.canConfirm();
    const walletShort = walletBalance < allocatedTotal;

    return (
      <div className="pd-page">
        <div className="pd-shell">
          <button type="button" className="pd-back" onClick={this.props.onClose}>
            <ArrowLeft size={16} /> Back to Auction Selection
          </button>

          <div className="pd-header">
            <h1>
              <GitBranch size={18} /> Payment Distribution
            </h1>
            <span className="pd-header__context">
              {this.contextLabel} &middot; {this.auctions.length} auction
              {this.auctions.length > 1 ? 's' : ''}
            </span>
          </div>

          <div className="pd-info-banner">
            <Lightbulb size={16} />
            <span>
              Enter how much you&apos;d like to pay for each auction below. Each amount can be
              anywhere from 0 up to what&apos;s due — pay one in full, pay several partially, or
              settle them all at once.
            </span>
          </div>

          <div className="pd-alloc-toolbar">
            <p>
              Paying <strong className="pd-alloc-toolbar__ok">{money(allocatedTotal)}</strong> of{' '}
              {money(totalDue)} OMR due
            </p>
            <div className="pd-alloc-toolbar__actions">
              <button type="button" onClick={this.handleResetToFullAmount}>
                Reset to Full Amount
              </button>
            </div>
          </div>

          <div className="pd-alloc-list">
            {this.auctions.map((a) => this.renderAllocationRow(a))}
          </div>

          {type === 'client' ? this.renderClientPanel() : this.renderMzadcomPanel()}

          <button
            type="button"
            className={`pd-confirm-btn${canConfirm ? '' : ' pd-confirm-btn--disabled'}`}
            disabled={!canConfirm || submitting}
            onClick={this.handleSubmit}
          >
            <CircleCheck size={18} />{' '}
            {submitting ? 'Submitting…' : `Confirm & Pay ${money(allocatedTotal)} OMR`}
          </button>

          {type === 'mzadcom' && method === 'wallet' && walletShort && (
            <p className="pd-confirm-hint">
              Wallet balance is insufficient — choose Online or Offline instead.
            </p>
          )}

          {!hasAllocation && (
            <p className="pd-confirm-hint">
              Enter a payment amount for at least one auction to proceed.
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default PaymentDistribution;
